import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useState } from "react";
import { motion } from "motion/react";

const FORM_ROUTING: Record<string, string> = {
  "+65": "https://readdy.ai/api/form/d90ggcobhec1he3opj60",
  "+1": "https://readdy.ai/api/form/d90ggcobhec1he3opj6g",
  "+63": "https://readdy.ai/api/form/d90ggcobhec1he3opj70",
  "+60": "https://readdy.ai/api/form/d90ggcobhec1he3opj7g",
};

const DEFAULT_FORM_URL = "https://readdy.ai/api/form/d90ggcobhec1he3opj60";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Read honeypot if filled, silently bail
    const honeypot = (formData.get("phone_alt") as string || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    // Pick the right form URL based on selected country code
    const countryCode = (formData.get("country-code") as string || "").trim();
    const submitUrl = FORM_ROUTING[countryCode] || DEFAULT_FORM_URL;

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "phone_alt" && typeof value === "string") params.append(key, value);
    });
    try {
      const response = await fetch(submitUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (response.ok) { setStatus("success"); form.reset(); }
      else { setStatus("error"); }
    } catch { setStatus("error"); }
  };
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-20 bg-primary relative overflow-hidden" style={{ minHeight: "320px" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-transparent" />
        <div className="relative w-full px-4 md:px-8 lg:px-12 py-16 md:py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Contact Us</h1>
            <p className="text-white/80 max-w-2xl mx-auto text-sm md:text-base">Reach out for a consultation, support, or to discuss your next AV project.</p>
            <div className="w-16 h-1 bg-accent mx-auto mt-6 rounded-full" />
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Our Offices</h2>
              <div className="space-y-6">
                {/* Singapore */}
                <div className="p-5 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-map-pin-line text-accent text-lg" />
                    <h3 className="text-sm font-bold text-gray-800">Fiber One Pte Ltd Singapore</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">No 1 Irving Place #08-13, The Commerze @ Irving, Singapore 369546</p>
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    <i className="ri-phone-line text-accent mr-1 text-sm"></i>+65 6635 2366
                  </p>
                  <a href="mailto:sales.sg@fiberoneglobal.com" className="text-sm text-gray-700 hover:text-accent transition-colors">
                    <i className="ri-mail-line text-accent mr-1 text-sm"></i>sales.sg@fiberoneglobal.com
                  </a>
                </div>

                {/* Chicago */}
                <div className="p-5 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-map-pin-line text-accent text-lg" />
                    <h3 className="text-sm font-bold text-gray-800">Fiber One Global Chicago</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">318 W. Adams Street, Suite 1600, Chicago, IL 60606 US</p>
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    <i className="ri-phone-line text-accent mr-1 text-sm"></i>+1 (312) 348-4447
                  </p>
                  <a href="mailto:sales.us@fiberoneglobal.com?cc=sales.sg@fiberoneglobal.com" className="text-sm text-gray-700 hover:text-accent transition-colors">
                    <i className="ri-mail-line text-accent mr-1 text-sm"></i>sales.us@fiberoneglobal.com
                  </a>
                </div>

                {/* Philippines */}
                <div className="p-5 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-map-pin-line text-accent text-lg" />
                    <h3 className="text-sm font-bold text-gray-800">Fiber One Philippines Pasig City</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">6/F D SunTree Tower, 13 Meralco Ave., Pasig City, Philippines 1600</p>
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    <i className="ri-phone-line text-accent mr-1 text-sm"></i>+63 917 594-6672
                  </p>
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    <i className="ri-phone-line text-accent mr-1 text-sm"></i>+63 976 535-9426
                  </p>
                  <a href="mailto:sales.ph@fiberoneglobal.com?cc=sales.sg@fiberoneglobal.com" className="text-sm text-gray-700 hover:text-accent transition-colors block">
                    <i className="ri-mail-line text-accent mr-1 text-sm"></i>sales.ph@fiberoneglobal.com
                  </a>
                </div>

                {/* Malaysia */}
                <div className="p-5 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-map-pin-line text-accent text-lg" />
                    <h3 className="text-sm font-bold text-gray-800">Fiber One Malaysia Kuala Lumpur</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">Level 23 -1, Premier Suite One Mont Kiara. No. 1, Jalan Kiara Mont Kiara. 50480 KL.</p>
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    <i className="ri-phone-line text-accent mr-1 text-sm"></i>+6013 284 8997
                  </p>
                  <a href="mailto:sales.my@fiberoneglobal.com?cc=sales.sg@fiberoneglobal.com" className="text-sm text-gray-700 hover:text-accent transition-colors">
                    <i className="ri-mail-line text-accent mr-1 text-sm"></i>sales.my@fiberoneglobal.com
                  </a>
                </div>
              </div>

              <div className="mt-8 p-5 rounded-xl bg-gray-50 border border-gray-100">
                <h3 className="text-sm font-bold text-gray-800 mb-2">Emergency Support</h3>
                <p className="text-sm text-gray-600 mb-3">Managed Services clients have 24/7 hotline access. Response time guaranteed under 15 minutes for critical systems.</p>
                <div className="flex items-center gap-2 text-sm">
                  <i className="ri-whatsapp-line text-green-600 text-lg"></i>
                  <span className="text-gray-700">Contact AVA</span>
                  <a
                    href="https://wa.me/6581138215"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent font-semibold hover:underline transition-all cursor-pointer"
                  >
                    +65 8113 8215
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="lg:col-span-2"
            >
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Send a Message</h2>
              <form id="fiberone-contact" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                    <input type="text" name="your-name" required className="w-full px-4 py-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 transition-all duration-200 hover:border-gray-300" placeholder="Your name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                    <input type="email" name="email" required className="w-full px-4 py-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 transition-all duration-200 hover:border-gray-300" placeholder="Your email" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number <span className="text-red-500">*</span></label>
                    <div className="flex gap-2">
                      <select name="country-code" required className="w-[110px] flex-shrink-0 px-2 py-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 transition-all duration-200 hover:border-gray-300 bg-white cursor-pointer appearance-none" style={{ backgroundImage: "url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%236b7280%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Cpolyline points=%226 9 12 15 18 9%22/%3E%3C/svg%3E')", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center", paddingRight: "28px" }}>
                        <option value="+65">+65</option>
                        <option value="+1">+1</option>
                        <option value="+63">+63</option>
                        <option value="+60">+60</option>
                        <option value="+86">+86</option>
                        <option value="+91">+91</option>
                        <option value="+81">+81</option>
                        <option value="+82">+82</option>
                        <option value="+44">+44</option>
                        <option value="+61">+61</option>
                        <option value="+62">+62</option>
                        <option value="+66">+66</option>
                        <option value="+84">+84</option>
                        <option value="+971">+971</option>
                      </select>
                      <input type="tel" name="contact" required className="flex-1 min-w-0 px-4 py-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 transition-all duration-200 hover:border-gray-300" placeholder="Your contact number" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                    <input type="text" name="your-company" className="w-full px-4 py-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 transition-all duration-200 hover:border-gray-300" placeholder="Your company" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Message</label>
                  <textarea name="your-message" rows={5} maxLength={500} className="w-full px-4 py-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 transition-all duration-200 hover:border-gray-300 resize-y" placeholder="Tell us about your project..."></textarea>
                  <p className="text-xs text-gray-400 mt-1">Maximum 500 characters</p>
                </div>
                {/* anti-spam honeypot */}
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute opacity-0 pointer-events-none top-[-9999px] left-[-9999px]" />
                <div className="text-left">
                  <button type="submit" disabled={status === "submitting"} className="inline-block px-10 py-3 bg-accent text-white font-semibold rounded-md hover:bg-accent-light transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60">
                    {status === "submitting" ? "Sending..." : "Send Message"}
                  </button>
                </div>
                {status === "success" && (
                  <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center text-sm text-green-600 bg-green-50 py-3 rounded-md">
                    Thank you! Your message has been sent successfully.
                  </motion.p>
                )}
                {status === "error" && (
                  <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center text-sm text-red-600 bg-red-50 py-3 rounded-md">
                    Something went wrong. Please try again.
                  </motion.p>
                )}
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}